import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, signIn } from '../../../firebase';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { Dialog, DialogActions, DialogContent } from '@mui/material';
import GeneralText from '../../../stories/general-components/GeneralText';
import TextField from '../../../stories/general-components/TextField';
import Button from '../../../stories/general-components/Button';
import { useSelector } from 'react-redux';

const SignIn = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lockBtn, setLockBtn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordView, setPasswordView] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const businessId = localStorage.getItem('businessId');

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const currentPlatform = useSelector(state => state.core.currentPlatform);

  const mainColorLocalStorage = localStorage.getItem('mainColor');
  const secColorLocalStorage = localStorage.getItem('secColor');

  const signInUser = async (email, password) => {
    try {
      await signIn(auth, email, password);
      localStorage.removeItem('businessId');
      sessionStorage.removeItem('businessToken');
      navigate('/select-business');
    } catch (error) {
      console.error('Error signing in: ', error?.code);
      setLockBtn(false);
      setErrorMessage(t(error?.code));
    }
  };

  const businessUserLogin = async () => {
    try {
      setLockBtn(true);
      signInUser(email, password);
    } catch (error) {
      setLockBtn(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleReset = () => {
    localStorage.removeItem('businessId');
    window.location.reload();
  };

  return (
    <div style={{ width: '100%' }}>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>{t('businessChanged')}</DialogContent>
        <DialogActions>
          <Button label={t('reset')} onClick={handleReset} color="primary" />
          <Button
            label={t('cancel')}
            onClick={handleClose}
            color="primary"
            autoFocus
          />
        </DialogActions>
      </Dialog>

      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <div
          style={{
            borderRadius: '8px',
            padding: '15px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '25px',
          }}
          onClick={() => setIsOpen(true)}
        >
          <img
            src={`https://storage.googleapis.com/business-logos-node/${businessId || currentPlatform || 'nodetechnologies'}.png`}
            height="30px"
            alt={currentPlatform}
          />
        </div>
        <GeneralText
          text={t('welcomeBack')}
          fontSize="24px"
          size="bold"
          primary={true}
          classNameComponent="mt-3"
        />
        <GeneralText
          text={t('signInToContinue')}
          fontSize="14px"
          size="regular"
          primary={true}
          classNameComponent="mt-1"
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <TextField
          autoComplete="username"
          id="email"
          name="email"
          type="email"
          fullWidth
          label={t('email')}
          onChange={(e) => setEmail(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              borderRadius: '12px',
            },
          }}
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <TextField
          autoComplete="current-password"
          id="password"
          name="password"
          fullWidth
          iconEndButton2="Visibility"
          iconEndButton1="VisibilityOff"
          endIconAction={() => setPasswordView(!passwordView)}
          endIconButton={passwordView}
          type={passwordView === true ? 'text' : 'password'}
          label={t('password')}
          onChange={(e) => setPassword(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              borderRadius: '12px',
            },
          }}
        />
      </div>

      {errorMessage && (
        <div style={{
          marginBottom: '20px',
          padding: '10px',
          borderRadius: '8px',
          backgroundColor: isDarkMode ? 'rgba(255, 0, 0, 0.1)' : 'rgba(255, 0, 0, 0.05)',
          color: '#ff4444'
        }}>
          <GeneralText
            text={errorMessage}
            fontSize="12px"
            size="regular"
            primary={true}
          />
        </div>
      )}

      <Button
        label={t('login')}
        onClick={businessUserLogin}
        endIcon={'LockOutlined'}
        fullWidth
        color={mainColorLocalStorage || theme.palette.text.primary}
        disableElevation
        disabled={!email || !password || lockBtn}
        buttonSx={{
          height: '48px',
          borderRadius: '12px',
          textTransform: 'none',
          fontSize: '12px',
          backgroundColor: mainColorLocalStorage || theme.palette.text.primary,
          '&:hover': {
            backgroundColor: mainColorLocalStorage || theme.palette.text.primary,
          },
        }}
        currentLoading={lockBtn}
      />

      <div style={{
        marginTop: '20px',
        textAlign: 'center'
      }}>
        <GeneralText
          text={t('forgotPassword')}
          fontSize="14px"
          size="regular"
          color={isDarkMode ? '#ffffff' : '#696969'}
          primary={false}
          onClick={() => navigate('/forgot-password')}
          classNameComponent="hover"
        />
      </div>
    </div>
  );
};

export default SignIn;
